import { PRODUCT_COLOR, PRODUCT_LABEL, PRODUCT_ORDER, formatCount } from '../format';
import type { Product } from '../types';

/** Mode chips ("S-Bahn", "Bus", …) with live counts for the current viewport. */
export class ModeFilters {
  private readonly chips = new Map<Product, HTMLButtonElement>();
  private readonly resetChip: HTMLButtonElement;
  readonly active = new Set<Product>(PRODUCT_ORDER);

  constructor(
    private readonly root: HTMLElement,
    private readonly onChange: () => void,
  ) {
    this.resetChip = document.createElement('button');
    this.resetChip.type = 'button';
    this.resetChip.className = 'filter-chip';
    this.resetChip.setAttribute('aria-pressed', 'true');
    this.resetChip.textContent = 'Alle';
    this.resetChip.hidden = true;
    this.resetChip.addEventListener('click', () => {
      for (const product of PRODUCT_ORDER) this.active.add(product);
      this.syncPressed();
      this.onChange();
    });
    root.appendChild(this.resetChip);

    for (const product of PRODUCT_ORDER) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'filter-chip';
      chip.setAttribute('aria-pressed', 'true');
      chip.title = `${PRODUCT_LABEL[product]} ein- oder ausblenden (Klick mit Umschalt: nur dieses)`;
      chip.innerHTML =
        `<span class="swatch" style="background:${PRODUCT_COLOR[product]}"></span>` +
        `<span>${PRODUCT_LABEL[product]}</span><span class="count">0</span>`;

      chip.addEventListener('click', (event) => {
        const isolate = event.shiftKey || event.altKey;
        if (isolate) {
          const alreadyOnly = this.active.size === 1 && this.active.has(product);
          this.active.clear();
          if (alreadyOnly) for (const p of PRODUCT_ORDER) this.active.add(p);
          else this.active.add(product);
        } else if (this.active.has(product)) {
          this.active.delete(product);
        } else {
          this.active.add(product);
        }
        this.syncPressed();
        this.onChange();
      });

      this.chips.set(product, chip);
      root.appendChild(chip);
    }
  }

  private syncPressed(): void {
    for (const [product, chip] of this.chips) {
      chip.setAttribute('aria-pressed', this.active.has(product) ? 'true' : 'false');
    }
    this.resetChip.hidden = this.active.size === PRODUCT_ORDER.length;
  }

  has(product: Product): boolean {
    return this.active.has(product);
  }

  /** Chips with nothing to show are hidden unless the user switched them off. */
  setCounts(counts: Record<Product, number>): void {
    for (const [product, chip] of this.chips) {
      const count = counts[product] ?? 0;
      const countEl = chip.querySelector('.count');
      if (countEl) countEl.textContent = formatCount(count);
      chip.hidden = count === 0 && this.active.has(product);
    }
    this.root.hidden = [...this.chips.values()].every((chip) => chip.hidden);
  }
}
